import { useMemo, useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import { classNames } from "@app/utils/classNames";
import { logger } from "@app/services/logger";
import {
  getShadowStats,
  getShadowReadiness,
  getShadowComparison,
  getAiEngineDecisions,
  getLearningLedger,
  getPromptRules,
  getReevaluationTriggers,
  getConflictRecords,
  getDiscoveredPairs,
  type ShadowStats,
  type ShadowReadiness,
  type ComparisonEntry,
  type AiEngineDecision,
  type EngineStats,
  type DecisionDetail,
  type LearningLedgerEntry,
  type PromptRule,
  type ReevaluationTrigger,
  type ConflictRecord,
  type DiscoveredPair,
} from "@app/services/aiEngineApi";
import "./AiEngineMonitorTab.css";

const DECISIONS_PER_PAGE = 20;

type SubTab = "stats" | "learning-log" | "prompt-rules" | "reevaluation" | "conflicts" | "discovered-pairs";

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: "stats", label: "Stats" },
  { id: "learning-log", label: "Learning Log" },
  { id: "prompt-rules", label: "Prompt Rules" },
  { id: "reevaluation", label: "Re-evaluation" },
  { id: "conflicts", label: "Conflicts" },
  { id: "discovered-pairs", label: "Discovered Pairs" },
];

function formatTime(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    return isoString;
  }
}

function formatGain(value: number | null | undefined): string {
  if (value === null || value === undefined) return "--";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

function formatConfidence(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function getDecisionBadgeClass(decision: string): string {
  switch (decision) {
    case "accept":
      return "ai-engine-monitor-badge--accept";
    case "reject":
      return "ai-engine-monitor-badge--reject";
    case "skip":
      return "ai-engine-monitor-badge--skip";
    default:
      return "ai-engine-monitor-badge--pending";
  }
}

function getResultBadgeClass(result: string | null): string {
  switch (result) {
    case "win":
      return "ai-engine-monitor-badge--win";
    case "loss":
      return "ai-engine-monitor-badge--loss";
    default:
      return "ai-engine-monitor-badge--pending";
  }
}

function getVerdictClass(verdict: string): string {
  switch (verdict) {
    case "AI Correct":
      return "ai-engine-monitor-verdict--correct";
    case "AI Wrong":
      return "ai-engine-monitor-verdict--wrong";
    default:
      return "ai-engine-monitor-verdict--no-decision";
  }
}

function getModeLabel(mode: EngineStats["mode"]): string {
  switch (mode) {
    case "rule_based":
      return "Rule-Based";
    case "ai_technical":
      return "AI Technical";
    case "ai_orderbook":
      return "AI Orderbook";
  }
}

function getWinRateColor(rate: number): string {
  if (rate >= 60) return "rgba(34, 197, 94, 0.95)";
  if (rate >= 40) return "rgba(251, 191, 36, 0.95)";
  return "rgba(239, 68, 68, 0.95)";
}

function getLlmStatusColor(status: EngineStats["llm_api_status"]): string {
  switch (status) {
    case "connected":
      return "rgba(34, 197, 94, 0.95)";
    case "error":
      return "rgba(239, 68, 68, 0.95)";
    case "quota_exceeded":
      return "rgba(251, 191, 36, 0.95)";
  }
}

function getLlmStatusLabel(status: EngineStats["llm_api_status"]): string {
  switch (status) {
    case "connected":
      return "Connected";
    case "error":
      return "Error";
    case "quota_exceeded":
      return "Quota Exceeded";
  }
}

function buildDecisionDetail(
  decision: AiEngineDecision,
): DecisionDetail {
  return {
    id: decision.id,
    pair: decision.pair,
    decision: decision.decision,
    direction: decision.direction ?? "long",
    confidence: decision.confidence,
    estimated_gain_pct: decision.estimated_gain_pct,
    reasoning: decision.reasoning,
    full_reasoning: decision.reasoning,
    created_at: decision.created_at,
    was_shadow: decision.was_shadow,
    actual_result: decision.actual_result,
    actual_gain_pct: decision.actual_gain_pct,
    tokens_used: decision.tokens_used ?? 0,
    cost: decision.cost ?? 0,
    latency_ms: decision.latency_ms ?? 0,
    snapshot_data: decision.snapshot_data ?? null,
  };
}

interface EnhancedStatsDashboardProps {
  stats: ShadowStats;
  decisions: AiEngineDecision[];
}

function EnhancedStatsDashboard({ stats, decisions }: EnhancedStatsDashboardProps) {
  const engineStats = useMemo<EngineStats>(() => {
    const accepted = stats.accepted;
    const rejected = stats.rejected;
    const total = stats.total_analyzed;
    const acceptRate = total > 0 ? (accepted / total) * 100 : 0;

    const avgConfidence = decisions.length > 0
      ? decisions.reduce((sum, d) => sum + d.confidence, 0) / decisions.length
      : 0;

    const resolvedDecisions = decisions.filter((d) => d.actual_result !== null);
    const correctCount = resolvedDecisions.filter((d) => {
      if (d.actual_result === "win" && d.decision === "accept") return true;
      if (d.actual_result === "loss" && d.decision === "reject") return true;
      return false;
    }).length;
    const winRate = resolvedDecisions.length > 0
      ? (correctCount / resolvedDecisions.length) * 100
      : stats.theoretical_win_rate;

    const accuracy = stats.total_accuracy > 0 ? stats.total_accuracy : 0;

    return {
      mode: "ai_technical",
      trades_analyzed_today: total,
      trades_accepted_today: accepted,
      trades_rejected_today: rejected,
      accept_rate: acceptRate,
      avg_ai_confidence: avgConfidence,
      win_rate: winRate,
      prediction_accuracy: accuracy,
      target_prediction_accuracy: 75,
      daily_api_calls: decisions.filter((d) => (d.tokens_used ?? 0) > 0).length || total,
      max_daily_api_calls: 500,
      daily_cost: parseFloat(decisions.reduce((sum, d) => sum + (d.cost ?? 0), 0).toFixed(4)),
      max_daily_cost: 5.0,
      llm_api_status: "connected",
      shadow_mode: true,
      custom_instructions_active: true,
    };
  }, [stats, decisions]);

  const modeIsActive = engineStats.mode === "ai_technical" || engineStats.mode === "ai_orderbook";

  const shadowDays = stats.shadow_days_active;
  const watchdogWarning = shadowDays < 3;
  const winRateLow = engineStats.win_rate < 50 && shadowDays < 3;

  return (
    <div className="ai-engine-monitor-dashboard-grid">
      {watchdogWarning && (
        <div
          className="ai-engine-monitor-stat-cell ai-engine-monitor-stat-cell--span2"
          style={{
            border: winRateLow
              ? "1px solid rgba(239, 68, 68, 0.5)"
              : "1px solid rgba(251, 191, 36, 0.4)",
            background: winRateLow
              ? "rgba(239, 68, 68, 0.08)"
              : "rgba(251, 191, 36, 0.06)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <span
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: winRateLow ? "rgba(239, 68, 68, 0.95)" : "rgba(251, 191, 36, 0.95)",
              }}
            >
              Watchdog
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                color: "rgba(148, 163, 184, 0.7)",
                fontWeight: 500,
              }}
            >
              Days since AI engine transition: {shadowDays}
            </span>
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: winRateLow ? "rgba(239, 68, 68, 0.9)" : "rgba(251, 191, 36, 0.85)",
              fontWeight: 500,
            }}
          >
            Watchdog active: monitoring AI win rate
          </div>
          {winRateLow && (
            <div
              style={{
                fontSize: "0.75rem",
                color: "rgba(239, 68, 68, 0.9)",
                fontWeight: 600,
                marginTop: 4,
              }}
            >
              Alert: AI win rate ({engineStats.win_rate.toFixed(1)}%) is below 50% threshold
            </div>
          )}
        </div>
      )}

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Engine Mode</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: "1rem",
            fontWeight: 700,
            color: modeIsActive ? "rgba(34, 197, 94, 0.95)" : "rgba(148, 163, 184, 0.7)",
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: modeIsActive ? "rgba(34, 197, 94, 0.95)" : "rgba(148, 163, 184, 0.5)",
              display: "inline-block",
            }}
          />
          {getModeLabel(engineStats.mode)}
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Trades Today</span>
        <span className="ai-engine-monitor-stat-cell__value">{engineStats.trades_analyzed_today}</span>
        <span style={{ fontSize: "0.65rem", color: "rgba(148, 163, 184, 0.65)" }}>
          Accepted: {engineStats.trades_accepted_today} / Rejected: {engineStats.trades_rejected_today}
        </span>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: engineStats.accept_rate >= 50 ? "rgba(34, 197, 94, 0.9)" : "rgba(251, 191, 36, 0.9)",
          }}
        >
          Accept Rate: {engineStats.accept_rate.toFixed(1)}%
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Avg AI Confidence</span>
        <span className="ai-engine-monitor-stat-cell__value">
          {formatConfidence(engineStats.avg_ai_confidence)}
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Win Rate</span>
        <span
          className="ai-engine-monitor-stat-cell__value"
          style={{ color: getWinRateColor(engineStats.win_rate) }}
        >
          {engineStats.win_rate.toFixed(1)}%
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Prediction Accuracy</span>
        <span
          className="ai-engine-monitor-stat-cell__value"
          style={{ color: getWinRateColor(engineStats.prediction_accuracy) }}
        >
          {engineStats.prediction_accuracy.toFixed(1)}%
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Target Accuracy</span>
        <span className="ai-engine-monitor-stat-cell__value ai-engine-monitor-stat-cell__value--small">
          {engineStats.target_prediction_accuracy}%
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell ai-engine-monitor-stat-cell--span2">
        <span className="ai-engine-monitor-stat-cell__label">Daily API Calls</span>
        <div className="ai-engine-monitor-progress-bar-container">
          <div className="ai-engine-monitor-progress-bar-label">
            <span>{engineStats.daily_api_calls.toLocaleString()} / {engineStats.max_daily_api_calls.toLocaleString()}</span>
            <span>{((engineStats.daily_api_calls / engineStats.max_daily_api_calls) * 100).toFixed(1)}%</span>
          </div>
          <div className="ai-engine-monitor-progress-bar-track">
            <div
              className="ai-engine-monitor-progress-bar-fill"
              style={{
                width: `${Math.min((engineStats.daily_api_calls / engineStats.max_daily_api_calls) * 100, 100)}%`,
                background: engineStats.daily_api_calls / engineStats.max_daily_api_calls > 0.85
                  ? "linear-gradient(90deg, rgba(251, 191, 36, 0.7), rgba(251, 191, 36, 0.9))"
                  : "linear-gradient(90deg, rgba(56, 189, 248, 0.6), rgba(56, 189, 248, 0.85))",
              }}
            />
          </div>
        </div>
      </div>

      <div className="ai-engine-monitor-stat-cell ai-engine-monitor-stat-cell--span2">
        <span className="ai-engine-monitor-stat-cell__label">Daily Cost</span>
        <div className="ai-engine-monitor-progress-bar-container">
          <div className="ai-engine-monitor-progress-bar-label">
            <span>${engineStats.daily_cost.toFixed(4)} / ${engineStats.max_daily_cost.toFixed(2)}</span>
            <span>{((engineStats.daily_cost / engineStats.max_daily_cost) * 100).toFixed(1)}%</span>
          </div>
          <div className="ai-engine-monitor-progress-bar-track">
            <div
              className="ai-engine-monitor-progress-bar-fill"
              style={{
                width: `${Math.min((engineStats.daily_cost / engineStats.max_daily_cost) * 100, 100)}%`,
                background: engineStats.daily_cost / engineStats.max_daily_cost > 0.85
                  ? "linear-gradient(90deg, rgba(239, 68, 68, 0.7), rgba(239, 68, 68, 0.9))"
                  : "linear-gradient(90deg, rgba(34, 197, 94, 0.6), rgba(34, 197, 94, 0.85))",
              }}
            />
          </div>
        </div>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">LLM API Status</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: getLlmStatusColor(engineStats.llm_api_status),
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: getLlmStatusColor(engineStats.llm_api_status),
              display: "inline-block",
            }}
          />
          {getLlmStatusLabel(engineStats.llm_api_status)}
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Shadow Mode</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: engineStats.shadow_mode ? "rgba(56, 189, 248, 0.95)" : "rgba(34, 197, 94, 0.95)",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: engineStats.shadow_mode ? "rgba(56, 189, 248, 0.95)" : "rgba(34, 197, 94, 0.95)",
              display: "inline-block",
            }}
          />
          {engineStats.shadow_mode ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="ai-engine-monitor-stat-cell">
        <span className="ai-engine-monitor-stat-cell__label">Custom Instructions</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: engineStats.custom_instructions_active
              ? "rgba(34, 197, 94, 0.95)"
              : "rgba(148, 163, 184, 0.7)",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: engineStats.custom_instructions_active
                ? "rgba(34, 197, 94, 0.95)"
                : "rgba(148, 163, 184, 0.5)",
              display: "inline-block",
            }}
          />
          {engineStats.custom_instructions_active ? "Active" : "Inactive"}
        </span>
      </div>
    </div>
  );
}

function ComparisonTable({ entries }: { entries: ComparisonEntry[] }) {
  const summary = useMemo(() => {
    const total = entries.length;
    const correct = entries.filter((e) => e.verdict === "AI Correct").length;
    const wrong = entries.filter((e) => e.verdict === "AI Wrong").length;
    const accuracy = total > 0 ? ((correct / total) * 100).toFixed(1) : "0.0";
    return { total, correct, wrong, accuracy };
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No comparison data yet. Shadow mode needs to analyze some trades first.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-comparison-summary">
        <span>
          Total comparisons: <strong>{summary.total}</strong>
        </span>
        <span>
          AI Correct: <strong style={{ color: "rgba(34, 197, 94, 0.95)" }}>{summary.correct}</strong>
        </span>
        <span>
          AI Wrong: <strong style={{ color: "rgba(239, 68, 68, 0.95)" }}>{summary.wrong}</strong>
        </span>
        <span>
          AI Accuracy: <strong>{summary.accuracy}%</strong>
        </span>
      </div>
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Pair</th>
              <th>AI Decision</th>
              <th>AI Confidence</th>
              <th>AI Gain</th>
              <th>RB Result</th>
              <th>RB Gain</th>
              <th>Verdict</th>
              <th>Conflict Type</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, idx) => (
              <tr key={`${entry.pair}-${idx}`}>
                <td>{entry.pair}</td>
                <td>{entry.ai_decision}</td>
                <td>{formatConfidence(entry.ai_confidence)}</td>
                <td>{formatGain(entry.ai_estimated_gain)}</td>
                <td>{entry.rule_based_result}</td>
                <td>{formatGain(entry.rule_based_gain_pct)}</td>
                <td>
                  <span className={classNames("ai-engine-monitor-verdict", getVerdictClass(entry.verdict))}>
                    {entry.verdict === "No AI Decision" ? "No AI Decision" : entry.verdict}
                  </span>
                </td>
                <td>{entry.conflict_type ?? "--"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReadinessSection({ readiness }: { readiness: ShadowReadiness }) {
  const conditions = readiness.conditions;

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-readiness-grid">
        <div className="ai-engine-monitor-progress-item">
          <div className="ai-engine-monitor-progress-header">
            <span>
              <strong>Time (days)</strong>: {conditions.time.current_days} / {conditions.time.required_days}
            </span>
            <span>
              {conditions.time.met && <span style={{ color: "rgba(34, 197, 94, 0.95)" }}>&#10003;</span>}
            </span>
          </div>
          <div className="ai-engine-monitor-progress-bar-track">
            <div
              className={classNames(
                "ai-engine-monitor-progress-bar-fill",
                conditions.time.met
                  ? "ai-engine-monitor-progress-bar-fill--met"
                  : "ai-engine-monitor-progress-bar-fill--not-met",
              )}
              style={{ width: `${Math.min(conditions.time.progress_pct, 100)}%` }}
            />
          </div>
        </div>

        <div className="ai-engine-monitor-progress-item">
          <div className="ai-engine-monitor-progress-header">
            <span>
              <strong>Trades</strong>: {conditions.trades.current_trades} / {conditions.trades.required_trades}
            </span>
            <span>
              {conditions.trades.met && <span style={{ color: "rgba(34, 197, 94, 0.95)" }}>&#10003;</span>}
            </span>
          </div>
          <div className="ai-engine-monitor-progress-bar-track">
            <div
              className={classNames(
                "ai-engine-monitor-progress-bar-fill",
                conditions.trades.met
                  ? "ai-engine-monitor-progress-bar-fill--met"
                  : "ai-engine-monitor-progress-bar-fill--not-met",
              )}
              style={{ width: `${Math.min(conditions.trades.progress_pct, 100)}%` }}
            />
          </div>
        </div>

        <div className="ai-engine-monitor-progress-item">
          <div className="ai-engine-monitor-progress-header">
            <span>
              <strong>Win Rate</strong>: {conditions.win_rate.current_win_rate}% / {conditions.win_rate.required_win_rate}%
            </span>
            <span>
              {conditions.win_rate.met && <span style={{ color: "rgba(34, 197, 94, 0.95)" }}>&#10003;</span>}
            </span>
          </div>
          <div className="ai-engine-monitor-progress-bar-track">
            <div
              className={classNames(
                "ai-engine-monitor-progress-bar-fill",
                conditions.win_rate.met
                  ? "ai-engine-monitor-progress-bar-fill--met"
                  : "ai-engine-monitor-progress-bar-fill--not-met",
              )}
              style={{ width: `${Math.min(conditions.win_rate.progress_pct, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {readiness.ready && (
        <div className="ai-engine-monitor-readiness-banner">
          All conditions met. Shadow mode is ready for transition.
        </div>
      )}
    </div>
  );
}

interface DecisionsTableWithFiltersProps {
  decisions: AiEngineDecision[];
  onViewFull: (detail: DecisionDetail) => void;
}

function DecisionsTableWithFilters({ decisions, onViewFull }: DecisionsTableWithFiltersProps) {
  const [selectedPair, setSelectedPair] = useState<string>("all");
  const [selectedDecision, setSelectedDecision] = useState<string>("all");
  const [selectedResult, setSelectedResult] = useState<string>("all");
  const [selectedShadow, setSelectedShadow] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);

  const uniquePairs = useMemo(() => {
    const pairs = new Set(decisions.map((d) => d.pair));
    return Array.from(pairs).sort();
  }, [decisions]);

  const filteredDecisions = useMemo(() => {
    let filtered = [...decisions];

    if (selectedPair !== "all") {
      filtered = filtered.filter((d) => d.pair === selectedPair);
    }

    if (selectedDecision !== "all") {
      filtered = filtered.filter((d) => d.decision === selectedDecision);
    }

    if (selectedResult !== "all") {
      filtered = filtered.filter((d) => {
        if (selectedResult === "pending") return d.actual_result === null;
        return d.actual_result === selectedResult;
      });
    }

    if (selectedShadow !== "all") {
      const isShadow = selectedShadow === "shadow";
      filtered = filtered.filter((d) => d.was_shadow === isShadow);
    }

    if (dateRange !== "all") {
      const now = Date.now();
      const rangeMs: Record<string, number> = {
        "24h": 24 * 60 * 60 * 1000,
        "7d": 7 * 24 * 60 * 60 * 1000,
        "30d": 30 * 24 * 60 * 60 * 1000,
      };
      const cutoff = now - (rangeMs[dateRange] ?? 0);
      filtered = filtered.filter((d) => new Date(d.created_at).getTime() >= cutoff);
    }

    filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return filtered;
  }, [decisions, selectedPair, selectedDecision, selectedResult, selectedShadow, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filteredDecisions.length / DECISIONS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedDecisions = filteredDecisions.slice(
    (safePage - 1) * DECISIONS_PER_PAGE,
    safePage * DECISIONS_PER_PAGE,
  );

  useEffect(() => {
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  }, [safePage, currentPage]);

  const handlePairChange = useCallback((value: string) => {
    setSelectedPair(value);
    setCurrentPage(1);
  }, []);

  const handleDecisionChange = useCallback((value: string) => {
    setSelectedDecision(value);
    setCurrentPage(1);
  }, []);

  const handleResultChange = useCallback((value: string) => {
    setSelectedResult(value);
    setCurrentPage(1);
  }, []);

  const handleShadowChange = useCallback((value: string) => {
    setSelectedShadow(value);
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback((value: string) => {
    setDateRange(value);
    setCurrentPage(1);
  }, []);

  const handleViewFull = useCallback(
    (decision: AiEngineDecision) => {
      onViewFull(buildDecisionDetail(decision));
    },
    [onViewFull],
  );

  const handlePrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  }, [totalPages]);

  if (decisions.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No AI decisions recorded yet.
      </div>
    );
  }

  return (
    <div>
      <div className="ai-engine-monitor-filter-bar">
        <select
          className="ai-engine-monitor-filter-select"
          value={selectedPair}
          onChange={(e) => handlePairChange(e.target.value)}
        >
          <option value="all">All Pairs</option>
          {uniquePairs.map((pair) => (
            <option key={pair} value={pair}>{pair}</option>
          ))}
        </select>

        <select
          className="ai-engine-monitor-filter-select"
          value={selectedDecision}
          onChange={(e) => handleDecisionChange(e.target.value)}
        >
          <option value="all">All Decisions</option>
          <option value="accept">Accept</option>
          <option value="reject">Reject</option>
          <option value="skip">Skip</option>
        </select>

        <select
          className="ai-engine-monitor-filter-select"
          value={selectedResult}
          onChange={(e) => handleResultChange(e.target.value)}
        >
          <option value="all">All Results</option>
          <option value="win">Win</option>
          <option value="loss">Loss</option>
          <option value="pending">Pending</option>
        </select>

        <select
          className="ai-engine-monitor-filter-select"
          value={selectedShadow}
          onChange={(e) => handleShadowChange(e.target.value)}
        >
          <option value="all">All Modes</option>
          <option value="shadow">Shadow</option>
          <option value="live">Live</option>
        </select>

        <select
          className="ai-engine-monitor-filter-select"
          value={dateRange}
          onChange={(e) => handleDateRangeChange(e.target.value)}
        >
          <option value="all">All Time</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Pair</th>
              <th>Direction</th>
              <th>Decision</th>
              <th>Confidence</th>
              <th>Est. Gain</th>
              <th>Reasoning</th>
              <th>Result</th>
              <th>Correct</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDecisions.map((d) => {
              const isCorrect: boolean | null = d.actual_result
                ? (d.actual_result === "win" && d.decision === "accept") ||
                  (d.actual_result === "loss" && d.decision === "reject")
                : null;

              return (
                <tr key={d.id}>
                  <td>{formatTime(d.created_at)}</td>
                  <td>{d.pair}</td>
                  <td>
                    <span
                      className={classNames(
                        "ai-engine-monitor-direction-arrow",
                        d.direction === "short"
                          ? "ai-engine-monitor-direction-arrow--short"
                          : "ai-engine-monitor-direction-arrow--long",
                      )}
                    >
                      {d.direction === "short" ? "\u2193 Short" : "\u2191 Long"}
                    </span>
                  </td>
                  <td>
                    <span className={classNames("ai-engine-monitor-badge", getDecisionBadgeClass(d.decision))}>
                      {d.decision}
                    </span>
                  </td>
                  <td>{formatConfidence(d.confidence)}</td>
                  <td>{formatGain(d.estimated_gain_pct)}</td>
                  <td>
                    <span style={{ color: "rgba(203, 213, 225, 0.75)", fontSize: "0.7rem" }}>
                      {(d.reasoning || "").length > 50
                        ? `${(d.reasoning || "").slice(0, 50)}...`
                        : d.reasoning || "--"}
                    </span>
                    {(d.reasoning || "").length > 50 && (
                      <button
                        className="ai-engine-monitor-view-full-btn"
                        style={{ marginLeft: 4 }}
                        onClick={() => handleViewFull(d)}
                      >
                        View Full
                      </button>
                    )}
                  </td>
                  <td>
                    {d.actual_result ? (
                      <span className={classNames("ai-engine-monitor-badge", getResultBadgeClass(d.actual_result))}>
                        {d.actual_result}
                      </span>
                    ) : (
                      <span className="ai-engine-monitor-badge ai-engine-monitor-badge--pending">Pending</span>
                    )}
                  </td>
                  <td>
                    {isCorrect === true && (
                      <span style={{ color: "rgba(34, 197, 94, 0.95)", fontWeight: 600, fontSize: "0.75rem" }}>
                        Yes
                      </span>
                    )}
                    {isCorrect === false && (
                      <span style={{ color: "rgba(239, 68, 68, 0.95)", fontWeight: 600, fontSize: "0.75rem" }}>
                        No
                      </span>
                    )}
                    {isCorrect === null && (
                      <span style={{ color: "rgba(148, 163, 184, 0.6)", fontSize: "0.7rem" }}>--</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="ai-engine-monitor-pagination">
          <button
            className="ai-engine-monitor-pagination__btn"
            onClick={handlePrevPage}
            disabled={safePage <= 1}
          >
            Previous
          </button>

          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 7) {
              pageNum = i + 1;
            } else if (safePage <= 4) {
              pageNum = i + 1;
            } else if (safePage >= totalPages - 3) {
              pageNum = totalPages - 6 + i;
            } else {
              pageNum = safePage - 3 + i;
            }

            return (
              <button
                key={pageNum}
                className={classNames(
                  "ai-engine-monitor-pagination__btn",
                  safePage === pageNum && "ai-engine-monitor-pagination__btn--active",
                )}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            className="ai-engine-monitor-pagination__btn"
            onClick={handleNextPage}
            disabled={safePage >= totalPages}
          >
            Next
          </button>

          <span className="ai-engine-monitor-pagination__info">
            {filteredDecisions.length} total decisions
          </span>
        </div>
      )}
    </div>
  );
}

interface DecisionDetailModalProps {
  detail: DecisionDetail | null;
  onClose: () => void;
}

function DecisionDetailModal({ detail, onClose }: DecisionDetailModalProps) {
  useEffect(() => {
    if (!detail) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [detail, onClose]);

  if (!detail) return null;

  return (
    <div className="ai-engine-monitor-modal-overlay" onClick={onClose}>
      <div className="ai-engine-monitor-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="ai-engine-monitor-modal-header">
          <h3>Decision Detail: {detail.pair}</h3>
          <button className="ai-engine-monitor-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="ai-engine-monitor-modal-body">
          <div className="ai-engine-monitor-modal-section">
            <span className="ai-engine-monitor-modal-section__title">Full Reasoning</span>
            <div className="ai-engine-monitor-modal-reasoning">
              {detail.full_reasoning || detail.reasoning}
            </div>
          </div>

          {detail.snapshot_data && Object.keys(detail.snapshot_data).length > 0 && (
            <div className="ai-engine-monitor-modal-section">
              <span className="ai-engine-monitor-modal-section__title">Snapshot Data</span>
              <div className="ai-engine-monitor-modal-snapshot">
                {JSON.stringify(detail.snapshot_data, null, 2)}
              </div>
            </div>
          )}

          <div className="ai-engine-monitor-modal-section">
            <span className="ai-engine-monitor-modal-section__title">Metadata</span>
            <div className="ai-engine-monitor-modal-detail-grid">
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Tokens Used</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {detail.tokens_used.toLocaleString()}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Cost</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  ${detail.cost.toFixed(6)}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Latency</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {detail.latency_ms.toFixed(0)} ms
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Decision</span>
                <span className="ai-engine-monitor-modal-detail-item__value" style={{ textTransform: "capitalize" }}>
                  {detail.decision}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Direction</span>
                <span className="ai-engine-monitor-modal-detail-item__value" style={{ textTransform: "capitalize" }}>
                  {detail.direction}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Confidence</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {formatConfidence(detail.confidence)}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Est. Gain</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {formatGain(detail.estimated_gain_pct)}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Mode</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {detail.was_shadow ? "Shadow" : "Live"}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Actual Result</span>
                <span className="ai-engine-monitor-modal-detail-item__value" style={{ textTransform: "capitalize" }}>
                  {detail.actual_result ?? "Pending"}
                </span>
              </div>
              <div className="ai-engine-monitor-modal-detail-item">
                <span className="ai-engine-monitor-modal-detail-item__label">Actual Gain</span>
                <span className="ai-engine-monitor-modal-detail-item__value">
                  {formatGain(detail.actual_gain_pct)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LearningLogTab(): JSX.Element {
  const { data: entries, isLoading, error } = useQuery<LearningLedgerEntry[]>({
    queryKey: ["learningLedger"],
    queryFn: getLearningLedger,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (error) logger.error("Failed to load learning ledger:", error);
  }, [error]);

  if (isLoading) {
    return <div className="ai-engine-monitor-loading">Loading learning ledger...</div>;
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No learning ledger entries available. The AI engine learning system may not be active yet.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Pair</th>
              <th>Result</th>
              <th>Prediction Accuracy</th>
              <th>Gain</th>
              <th>Analysis</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, idx) => (
              <tr key={`${entry.pair}-${idx}`}>
                <td>{entry.pair}</td>
                <td>
                  <span className={classNames("ai-engine-monitor-badge", getResultBadgeClass(entry.result))}>
                    {entry.result}
                  </span>
                </td>
                <td>
                  <span style={{ color: getWinRateColor(entry.prediction_accuracy), fontWeight: 600 }}>
                    {entry.prediction_accuracy.toFixed(1)}%
                  </span>
                </td>
                <td>{formatGain(entry.gain)}</td>
                <td style={{ maxWidth: 300, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {entry.analysis}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PromptRulesTab(): JSX.Element {
  const { data: rules, isLoading, error } = useQuery<PromptRule[]>({
    queryKey: ["promptRules"],
    queryFn: getPromptRules,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (error) logger.error("Failed to load prompt rules:", error);
  }, [error]);

  if (isLoading) {
    return <div className="ai-engine-monitor-loading">Loading prompt rules...</div>;
  }

  if (!rules || rules.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No prompt rules available. The AI engine custom instructions system may not be configured.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Rule Text</th>
              <th>Type</th>
              <th>Source</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule, idx) => (
              <tr key={`rule-${idx}`}>
                <td style={{ maxWidth: 350, whiteSpace: "normal", wordBreak: "break-word" }}>
                  {rule.rule_text}
                </td>
                <td>{rule.type}</td>
                <td>{rule.source}</td>
                <td>
                  <span
                    style={{
                      color: rule.active ? "rgba(34, 197, 94, 0.95)" : "rgba(148, 163, 184, 0.7)",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {rule.active ? "Active" : "Inactive"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReevaluationTab(): JSX.Element {
  const { data: triggers, isLoading, error } = useQuery<ReevaluationTrigger[]>({
    queryKey: ["reevaluationTriggers"],
    queryFn: getReevaluationTriggers,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (error) logger.error("Failed to load reevaluation triggers:", error);
  }, [error]);

  if (isLoading) {
    return <div className="ai-engine-monitor-loading">Loading reevaluation triggers...</div>;
  }

  if (!triggers || triggers.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No reevaluation trigger data available.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Trigger Type</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {triggers.map((trigger, idx) => (
              <tr key={`trigger-${idx}`}>
                <td>
                  <span style={{ textTransform: "capitalize", fontWeight: 500 }}>
                    {trigger.trigger_type.replace(/_/g, " ")}
                  </span>
                </td>
                <td>
                  <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                    {trigger.count.toLocaleString()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DiscoveredPairsTab(): JSX.Element {
  const { data: pairs, isLoading, error } = useQuery<DiscoveredPair[]>({
    queryKey: ["discoveredPairs"],
    queryFn: getDiscoveredPairs,
    refetchInterval: 120_000,
  });

  useEffect(() => {
    if (error) logger.error("Failed to load discovered pairs:", error);
  }, [error]);

  if (isLoading) {
    return <div className="ai-engine-monitor-loading">Loading discovered pairs...</div>;
  }

  if (!pairs || pairs.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No AI-discovered pairs available yet. Pair discovery runs periodically.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Discovered At</th>
              <th>Pair</th>
              <th>Direction</th>
              <th>Decision</th>
              <th>Confidence</th>
              <th>Price</th>
              <th>Reasoning</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map((pair) => (
              <tr key={pair.id}>
                <td>{formatTime(pair.created_at)}</td>
                <td className="ai-engine-monitor-pair-cell">{pair.pair}</td>
                <td>{pair.direction}</td>
                <td>{pair.decision}</td>
                <td>{formatConfidence(pair.confidence)}</td>
                <td>{pair.current_price != null ? pair.current_price.toFixed(8) : "N/A"}</td>
                <td style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {pair.reasoning || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConflictsTab(): JSX.Element {
  const { data: records, isLoading, error } = useQuery<ConflictRecord[]>({
    queryKey: ["conflictRecords"],
    queryFn: getConflictRecords,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (error) logger.error("Failed to load conflict records:", error);
  }, [error]);

  if (isLoading) {
    return <div className="ai-engine-monitor-loading">Loading conflict records...</div>;
  }

  if (!records || records.length === 0) {
    return (
      <div className="ai-engine-monitor-empty">
        No conflict records available.
      </div>
    );
  }

  return (
    <div className="ai-engine-monitor-section">
      <div className="ai-engine-monitor-table-wrapper">
        <table className="ai-engine-monitor-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Pair</th>
              <th>AI Decision</th>
              <th>RB Decision</th>
              <th>Conflict Type</th>
              <th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.id}>
                <td>{formatTime(record.created_at)}</td>
                <td>{record.pair}</td>
                <td>{record.ai_decision}</td>
                <td>{record.rule_based_decision}</td>
                <td>{record.conflict_type}</td>
                <td>
                  <span
                    style={{
                      color: record.resolved ? "rgba(34, 197, 94, 0.95)" : "rgba(251, 191, 36, 0.9)",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    {record.resolved ? "Resolved" : "Unresolved"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AiEngineMonitorTab(): JSX.Element {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("stats");
  const [selectedDecisionDetail, setSelectedDecisionDetail] = useState<DecisionDetail | null>(null);

  const {
    data: shadowStats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery<ShadowStats>({
    queryKey: ["shadowStats"],
    queryFn: getShadowStats,
    refetchInterval: 30_000,
  });

  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    error: comparisonError,
  } = useQuery<ComparisonEntry[]>({
    queryKey: ["shadowComparison"],
    queryFn: getShadowComparison,
    refetchInterval: 60_000,
  });

  const {
    data: shadowReadiness,
    isLoading: readinessLoading,
    error: readinessError,
  } = useQuery<ShadowReadiness>({
    queryKey: ["shadowReadiness"],
    queryFn: getShadowReadiness,
    refetchInterval: 60_000,
  });

  const {
    data: decisions,
    isLoading: decisionsLoading,
    error: decisionsError,
  } = useQuery<AiEngineDecision[]>({
    queryKey: ["aiEngineDecisions"],
    queryFn: getAiEngineDecisions,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (statsError) logger.error("Failed to load shadow stats:", statsError);
    if (comparisonError) logger.error("Failed to load shadow comparison:", comparisonError);
    if (readinessError) logger.error("Failed to load shadow readiness:", readinessError);
    if (decisionsError) logger.error("Failed to load AI engine decisions:", decisionsError);
  }, [statsError, comparisonError, readinessError, decisionsError]);

  const handleViewFull = useCallback((detail: DecisionDetail) => {
    setSelectedDecisionDetail(detail);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedDecisionDetail(null);
  }, []);

  function renderSubTabContent(): JSX.Element {
    switch (activeSubTab) {
      case "learning-log":
        return (
          <Card title="Learning Log">
            <LearningLogTab />
          </Card>
        );
      case "prompt-rules":
        return (
          <Card title="Prompt Rules">
            <PromptRulesTab />
          </Card>
        );
      case "reevaluation":
        return (
          <Card title="Re-evaluation Triggers">
            <ReevaluationTab />
          </Card>
        );
      case "conflicts":
        return (
          <Card title="AI Rule Conflicts">
            <ConflictsTab />
          </Card>
        );
      case "discovered-pairs":
        return (
          <Card title="AI Discovered Pairs">
            <DiscoveredPairsTab />
          </Card>
        );
      default:
        return (
          <>
            <Card title="Engine Stats Dashboard">
              {statsLoading ? (
                <div className="ai-engine-monitor-loading">Loading stats...</div>
              ) : shadowStats && decisions ? (
                <EnhancedStatsDashboard stats={shadowStats} decisions={decisions} />
              ) : (
                <div className="ai-engine-monitor-empty">Unable to load engine stats.</div>
              )}
            </Card>

            <Card title="AI vs Rule-Based Comparison">
              {comparisonLoading ? (
                <div className="ai-engine-monitor-loading">Loading comparison data...</div>
              ) : comparisonData ? (
                <ComparisonTable entries={comparisonData} />
              ) : (
                <div className="ai-engine-monitor-empty">Unable to load comparison data.</div>
              )}
            </Card>

            <Card title="Shadow Exit Readiness">
              {readinessLoading ? (
                <div className="ai-engine-monitor-loading">Loading readiness data...</div>
              ) : shadowReadiness ? (
                <ReadinessSection readiness={shadowReadiness} />
              ) : (
                <div className="ai-engine-monitor-empty">Unable to load readiness data.</div>
              )}
            </Card>

            <Card title="AI Engine Decisions">
              {decisionsLoading ? (
                <div className="ai-engine-monitor-loading">Loading decisions...</div>
              ) : decisions ? (
                <DecisionsTableWithFilters decisions={decisions} onViewFull={handleViewFull} />
              ) : (
                <div className="ai-engine-monitor-empty">Unable to load decisions data.</div>
              )}
            </Card>
          </>
        );
    }
  }

  return (
    <div className="ai-engine-monitor-root">
      <SectionHeader
        title="AI Engine Monitor"
        subtitle="AI Technical Engine shadow mode monitoring and performance tracking"
      />

      <div className="ai-engine-monitor-subtab-bar">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            className={classNames(
              "ai-engine-monitor-subtab-btn",
              activeSubTab === tab.id && "ai-engine-monitor-subtab-btn--active",
            )}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {renderSubTabContent()}

      <DecisionDetailModal
        detail={selectedDecisionDetail}
        onClose={handleCloseModal}
      />
    </div>
  );
}
